import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Mail,
  Phone,
  Sparkles,
  CheckCircle2,
  ArrowRight,
  ShieldCheck,
  Compass,
  MapPin,
  BarChart3,
  Briefcase,
  Coins,
  Search,
  Eye,
} from 'lucide-react';
import Layout from '../../components/layout/layout';
import Section from '../../components/ui/section';
import SectionBackground from '../../components/sections/section-background';
import HeroTemplateCandidats from '../../components/sections/HeroTemplateCandidats';
import TestimonialsSection from '../../components/sections/testimonials-section';
import CandidateTestimonial from '../../components/sections/CandidateTestimonial';
import { WebPageSchema, BreadcrumbSchema } from '../../lib/utils/schema-utils';

// — Typography aligned on home / campagnes / recrutement / externalisation —
const gradientText =
  'text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]';

const eyebrowClass =
  'inline-flex items-center rounded-full bg-white px-5 py-2 text-sm font-semibold text-neutral-500 uppercase tracking-widest shadow-sm ring-1 ring-neutral-200 mb-6';

const h2Class =
  'text-4xl md:text-5xl font-bold tracking-tight text-[#30475e] mb-5 leading-[1.08]';

const h3Class =
  'text-xl font-semibold text-[#30475e] mb-3 leading-snug';

const bodyClass =
  'text-base text-neutral-600 leading-relaxed';

const sectionSubtitleClass = 'text-lg md:text-xl text-neutral-600 leading-relaxed';

// — Section 2 — Pain points candidats (ce qui cloche) ————————————————
const painPoints = [
  {
    emoji: '🚫',
    metric: '81 %',
    title: 'Vous postulez. Silence radio.',
    description:
      "des candidats ont déjà été ghostés par une entreprise qui ne répond jamais. Comme si votre temps ne valait rien.",
  },
  {
    emoji: '🚫',
    metric: '49 %',
    title: 'Vous lisez l’annonce. La réalité est différente.',
    description:
      "des actifs ont déjà refusé une offre après avoir découvert le vrai contexte du poste. Trop tard pour réorienter sereinement leur recherche.",
  },
  {
    emoji: '🚫',
    metric: 'Flou',
    title: 'Vous parlez salaire. À tâtons.',
    description:
      "Le chiffre flou qui se précise au moment où il est trop tard pour partir. Le « on en discutera » qui devient « on a un budget plus serré ».",
  },
  {
    emoji: '🚫',
    metric: '1/5',
    title: 'Vous arrivez. Vous ne reconnaissez pas la promesse.',
    description:
      "des cadres quittent leur poste dans les 12 mois suivant l’embauche. Souvent parce que la réalité ne ressemble pas à ce qui avait été décrit.",
  },
];

// — Section 3 — Méthode candidat 6 étapes ————————————————————————————
const methodSteps = [
  {
    icon: Mail,
    title: 'Vous postulez. Vous recevez une vraie réponse.',
    description:
      "Sous 48 h ouvrées. Toujours. Même si ce n’est pas pour vous. Vous savez où vous en êtes, vous gardez la main sur votre recherche.",
  },
  {
    icon: Phone,
    title: 'Vous parlez 30 minutes avec un consultant senior.',
    description:
      "Pas un screening junior à 18 questions. Un échange humain où on vous explique vraiment le poste, l’équipe, le contexte, le pourquoi.",
  },
  {
    icon: Sparkles,
    title: 'Vous accédez à votre Parcours Découverte.',
    description:
      "Un environnement digital sur-mesure que vous traversez à votre rythme : présentation du dirigeant, immersion dans l’équipe, valeurs concrètes, mission narrée — et la grille de rémunération réelle.",
  },
  {
    icon: CheckCircle2,
    title: 'Vous décidez en connaissance de cause.',
    description:
      "À la sortie du parcours, vous re-confirmez votre engagement. Ou vous vous retirez sereinement. Sans pression, sans culpabilité, sans casser la relation pour la suite.",
  },
  {
    icon: ArrowRight,
    title: 'Vous signez en sachant exactement ce que vous signez.',
    description:
      "Plus de mauvaise surprise sur le package, le rythme, l’équipe, le management. Tout a été vu et discuté avant.",
  },
  {
    icon: ShieldCheck,
    title: 'Vous arrivez. Vous prenez votre poste sereinement.',
    description:
      "Onboarding préparé dès la signature, points de feedback structurés, objectifs clairs sur 90 jours. Vous ne ramez pas seul.",
  },
];

// — Section 4 — Engagements (4 cartes avec emoji léger) ————————————
const commitments = [
  {
    emoji: '✉️',
    title: 'Réponse à 100 % des candidatures sous 48 h ouvrées',
    description:
      "Aucune exception. Aucun ghosting. Même un refus est une réponse soignée — pour que vous puissiez continuer votre recherche sereinement.",
  },
  {
    emoji: '🤝',
    title: 'Brief honnête sur le poste, l’équipe, les contraintes',
    description:
      "Pas de marketing maquillé, pas de promesse vague. Vous recevez la vraie réalité — y compris ce qui peut vous freiner — pour décider en adulte.",
  },
  {
    emoji: '🔄',
    title: 'Debrief structuré à chaque étape du process',
    description:
      "Vous savez pourquoi vous avancez, ou pourquoi vous vous arrêtez. Avec des éléments concrets. Pour que vous progressiez, même si ce n’est pas pour cette fois.",
  },
  {
    emoji: '✨',
    title: 'Parcours Découverte avant l’entretien final',
    description:
      "Pas de signature à l’aveugle, plus de regret six mois après. Vous voyez la réalité avant de vous engager — et n’avancez que si vous êtes vraiment aligné.",
  },
];

// — Section 5 — Contenu du Parcours Découverte (5 modules type) ————————
const parcoursContents = [
  {
    icon: Compass,
    title: 'Les dates clés',
    description:
      "L’histoire vraie de l’entreprise, les virages, les moments fondateurs. Vous comprenez d’où elle vient avant de regarder où elle va.",
  },
  {
    icon: MapPin,
    title: 'L’ancrage territorial',
    description:
      "Où elle opère, sur quels marchés, avec quels clients-types. La géographie du métier au quotidien.",
  },
  {
    icon: BarChart3,
    title: 'Les chiffres clés',
    description:
      "CA, effectifs, croissance, marges si pertinent. La réalité économique brute, sans embellissement marketing.",
  },
  {
    icon: Briefcase,
    title: 'Votre mission, si vous l’acceptez',
    description:
      "Le poste transformé en mission narrative. Pas une fiche à remplir, un engagement à porter.",
  },
  {
    icon: Coins,
    title: 'La grille de rémunération',
    description:
      "Le vrai package, affiché avant la signature. Aucune surprise au moment de la table de négo. Aucun cabinet français ne le fait. Nous oui.",
  },
];

// — Section 6 — Pour qui c’est fait ————————————————————————————————
const fitProfiles = [
  {
    emoji: '⏱',
    title: 'Vous êtes en mobilité active',
    description:
      "Vous n’avez plus envie de perdre du temps sur des process opaques, des annonces vagues ou des entretiens à répétition sans feedback.",
  },
  {
    emoji: '🎯',
    title: 'Vous êtes un profil rare ou tendu',
    description:
      "IT, ingénieur, expert métier. Vous voulez choisir vraiment, pas subir. Vous savez ce que vous valez, vous voulez un process à la hauteur.",
  },
  {
    emoji: '👀',
    title: 'Vous êtes en observation discrète',
    description:
      "Vous regardez le marché sans urgence. Vous voulez comprendre une opportunité avant de bouger — sans engagement, sans pression.",
  },
  {
    emoji: '💔',
    title: 'Vous avez déjà été déçu',
    description:
      "Un précédent recrutement n’a pas tenu sa promesse. Vous ne voulez plus reproduire l’erreur. Cette fois, vous voulez voir avant de signer.",
  },
];

export default function PourquoiPage() {
  const navigate = useNavigate();

  return (
    <Layout
      title="Pourquoi candidater avec recruteurs.io | Sachez où vous mettez les pieds"
      description="Avant de signer, traversez notre Parcours Découverte. Réponse à 100 % des candidatures, brief honnête, grille de rémunération transparente. Le recrutement traité avec respect — à Marseille, Aix et PACA."
      keywords="candidat recrutement marseille, postuler cabinet recrutement, expérience candidat, parcours découverte candidat, transparence salaire, recrutement transparent, offres emploi cadre paca"
    >
      <WebPageSchema
        page={{
          title:
            'Pourquoi candidater avec recruteurs.io | Sachez où vous mettez les pieds',
          description:
            "recruteurs.io traite chaque candidat avec le respect que mérite son temps : réponse 100 % sous 48 h, brief honnête, Parcours Découverte avant la décision finale, grille de rémunération transparente. Marseille, Aix, PACA.",
          url: 'https://recruteurs.io/candidat/pourquoi',
          lastReviewed: '2026-05-10',
        }}
      />

      <BreadcrumbSchema
        items={[
          { name: 'Accueil', url: 'https://recruteurs.io' },
          { name: 'Candidat', url: 'https://recruteurs.io/candidat/pourquoi' },
          { name: 'Pourquoi recruteurs.io', url: 'https://recruteurs.io/candidat/pourquoi' },
        ]}
      />

      {/* HERO — image et couleurs candidat d'origine (pastel rose/jaune/bleu) */}
      <HeroTemplateCandidats
        page="candidat-pourquoi"
        title="Sachez où vous mettez les pieds. Avant de signer."
        eyebrow="Pour les candidats · Marseille · Aix · PACA"
        description="Dans le recrutement classique, on vous demande de candidater sur une fiche de poste, de répondre à des entretiens, de signer un contrat — sans que vous ayez vraiment vu l’entreprise, l’équipe, le quotidien, la rémunération. recruteurs.io fait l’inverse : avant que vous décidiez, vous traversez notre Parcours Découverte. Vous voyez la réalité. Vous décidez vraiment."
        imageUrl="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/pourquoi-recruteurs.io.webp"
        breadcrumbs={[
          { name: 'Candidat', href: '/candidat/pourquoi' },
          { name: 'Pourquoi recruteurs.io', href: '/candidat/pourquoi' },
        ]}
        cta1={{
          label: 'Voir les offres en cours',
          onClick: () => navigate('/candidat/offres'),
        }}
        cta2={{
          label: 'Comprendre le Parcours Découverte',
          onClick: () => navigate('/decouvrir'),
        }}
      />

      {/* SECTION 2 — Pain points */}
      <SectionBackground page="candidat-pourquoi" section="cp-pain" className="bg-white">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Ce que vous avez déjà vécu</span>

            <h2 className={h2Class}>
              Le recrutement classique vous demande de signer à l’aveugle.{' '}
              <span className={gradientText}>C’est anormal.</span>
            </h2>

            <p className={sectionSubtitleClass}>
              Quatre choses qu’on vous a probablement déjà fait subir — et qu’on ne
              vous fera jamais subir chez recruteurs.io.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {painPoints.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.5, delay: index * 0.06 }}
                className="rounded-[1.75rem] bg-white p-8 ring-1 ring-neutral-200 shadow-sm hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
              >
                <div className="mb-5 flex items-center justify-between">
                  <span className="text-3xl" aria-hidden>{item.emoji}</span>
                  <p className="text-3xl font-bold text-[#30475e] tracking-tight">
                    {item.metric}
                  </p>
                </div>

                <h3 className={h3Class}>{item.title}</h3>
                <p className={bodyClass}>{item.description}</p>
              </motion.div>
            ))}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 3 — Méthode candidat (6 étapes) */}
      <SectionBackground
        page="candidat-pourquoi"
        section="cp-method"
        className="bg-[#fbfaf8]"
      >
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Votre process, étape par étape</span>

            <h2 className={h2Class}>
              Six moments où on traite votre temps{' '}
              <span className={gradientText}>avec respect.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {methodSteps.map((step, index) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.6, delay: index * 0.07 }}
                  className="group rounded-[1.75rem] bg-white p-8 shadow-sm ring-1 ring-neutral-200 hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
                >
                  <div className="mb-8 flex items-center justify-between">
                    <div className="h-14 w-14 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white font-bold shadow-lg shadow-[#d03484]/20">
                      0{index + 1}
                    </div>
                    <Icon className="w-6 h-6 text-neutral-300 group-hover:text-[#d03484] transition-colors duration-300" />
                  </div>

                  <h3 className={h3Class}>{step.title}</h3>
                  <p className={bodyClass}>{step.description}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 4 — Nos 4 engagements */}
      <SectionBackground page="candidat-pourquoi" section="cp-commit" className="bg-white">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Ce qu’on s’engage à faire</span>

            <h2 className={h2Class}>
              Quatre promesses concrètes.{' '}
              <span className={gradientText}>Opposables. Vérifiables.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {commitments.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.5, delay: index * 0.06 }}
                className="rounded-[1.75rem] bg-[#fbfaf8] p-8 ring-1 ring-neutral-200 hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
              >
                <div className="mb-4 text-4xl" aria-hidden>{item.emoji}</div>
                <h3 className={h3Class}>{item.title}</h3>
                <p className={bodyClass}>{item.description}</p>
              </motion.div>
            ))}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 5 — Zoom Parcours Découverte (côté candidat) */}
      <SectionBackground
        page="candidat-pourquoi"
        section="cp-parcours"
        className="bg-[#fbfaf8]"
      >
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Le Parcours Découverte</span>

            <h2 className={h2Class}>
              Tout savoir{' '}
              <span className={gradientText}>avant de dire oui.</span>
            </h2>

            <p className={sectionSubtitleClass}>
              Le Parcours Découverte, c’est un environnement digital sur-mesure
              construit pour chaque entreprise cliente. Vous le suivez à votre
              rythme, depuis chez vous, à chaque heure du jour ou de la nuit où
              vous vous demandez «&nbsp;est-ce que je signe&nbsp;?&nbsp;».
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {parcoursContents.map((content, index) => {
              const Icon = content.icon;
              return (
                <motion.div
                  key={content.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: index * 0.07 }}
                  className="rounded-[1.75rem] bg-white p-8 ring-1 ring-neutral-200 shadow-sm hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white mb-5 shadow-lg shadow-[#d03484]/20">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className={h3Class}>{content.title}</h3>
                  <p className={bodyClass}>{content.description}</p>
                </motion.div>
              );
            })}
          </div>

          <div className="mt-12 rounded-[2rem] bg-white p-8 md:p-10 ring-1 ring-neutral-200 max-w-4xl mx-auto">
            <p className={`${bodyClass} text-center`}>
              <strong>À la fin, vous reprenez la parole.</strong> Vous dites
              pourquoi vous signez — ou pourquoi vous vous retirez. Et c’est
              cette parole-là qui fonde un recrutement durable, pour vous comme
              pour l’entreprise.
            </p>
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 6 — Pour qui */}
      <SectionBackground page="candidat-pourquoi" section="cp-fit" className="bg-white">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Vous êtes au bon endroit si&hellip;</span>

            <h2 className={h2Class}>
              Quatre profils pour qui notre approche{' '}
              <span className={gradientText}>change tout.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {fitProfiles.map((profile, index) => (
              <motion.div
                key={profile.title}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.5, delay: index * 0.06 }}
                className="rounded-[1.75rem] bg-[#fbfaf8] p-8 ring-1 ring-neutral-200 hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
              >
                <div className="mb-4 text-4xl" aria-hidden>{profile.emoji}</div>
                <h3 className={h3Class}>{profile.title}</h3>
                <p className={bodyClass}>{profile.description}</p>
              </motion.div>
            ))}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 7a — Témoignage candidat fort (S.L., 20 ans expérience commerciale) */}
      <CandidateTestimonial
        eyebrow="Ils en parlent mieux que nous"
        name="S. L."
        role="Développement commercial · 20 ans d'expérience"
        verified
        quote="Avec **plus de 20 ans** dans le développement commercial, j'ai eu affaire à de nombreux entretiens RH sous toutes les formes que l'on connaît. C'est **la première fois** que je note un parcours **interactif, clair et limpide** sur les tenants et aboutissants de l'entreprise qui recrute."
        background="white"
      />

      {/* SECTION 7b — Témoignages candidats (depuis Supabase, partagés avec la home) */}
      <div className="bg-[#fbfaf8]">
        <TestimonialsSection
          title="Ils ont été recrutés avec recruteurs.io"
          subtitle="Des candidats placés qui parlent de notre process — réponse, transparence, projection avant signature."
        />
      </div>

      {/* SECTION 8 — CTA final */}
      <SectionBackground page="candidat-pourquoi" section="cp-cta" className="bg-white">
        <Section>
          <div className="max-w-3xl mx-auto text-center">
            <h2 className={h2Class}>
              Prêt à postuler{' '}
              <span className={gradientText}>autrement&nbsp;?</span>
            </h2>
            <p className={`${sectionSubtitleClass} mb-10`}>
              Voyez les opportunités du moment, ou explorez d’abord notre
              Plateforme Parcours Découverte pour comprendre comment ça marche.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={() => navigate('/candidat/offres')}
                className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-6 py-2.5 text-sm font-bold whitespace-nowrap text-white shadow-lg shadow-[#d03484]/20 hover:shadow-xl hover:-translate-y-0.5 transition-all"
              >
                <Search className="w-5 h-5" />
                Voir les offres en cours
              </button>

              <button
                onClick={() => navigate('/decouvrir')}
                className="inline-flex items-center gap-2 rounded-2xl bg-white text-[#30475e] px-6 py-2.5 text-sm font-bold whitespace-nowrap ring-1 ring-neutral-200 hover:ring-neutral-300 hover:-translate-y-0.5 transition-all"
              >
                <Eye className="w-5 h-5" />
                Découvrir un parcours
              </button>
            </div>
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 9 — Bloc texte SEO de fin */}
      <SectionBackground page="candidat-pourquoi" section="cp-seo" className="bg-[#fbfaf8]">
        <Section>
          <div className="max-w-4xl mx-auto">
            <h2 className={`${h2Class} text-center`}>
              L’expérience candidat,{' '}
              <span className={gradientText}>autrement.</span>
            </h2>

            <div className="space-y-5 text-base md:text-lg text-neutral-700 leading-relaxed mt-10">
              <p>
                Vous cherchez un poste à Marseille, Aix-en-Provence ou ailleurs en
                PACA&nbsp;? Vous en avez assez des process de recrutement opaques,
                des annonces qui exagèrent et des entretiens où votre temps est
                visiblement secondaire&nbsp;? recruteurs.io a été conçu pour
                inverser ce rapport de force.
              </p>
              <p>
                Notre engagement est simple&nbsp;: <strong>une réponse à 100&nbsp;% des
                candidatures sous 48&nbsp;h ouvrées</strong>, un brief honnête sur le
                poste et l’équipe, un{' '}
                <a
                  href="/decouvrir"
                  className="font-semibold text-[#d03484] underline-offset-4 hover:underline"
                >
                  Parcours Découverte
                </a>{' '}
                pour voir la vraie réalité de l’entreprise avant de signer, et
                une grille de rémunération transparente — affichée avant la
                table de négociation, pas après.
              </p>
              <p>
                Voyez les{' '}
                <a
                  href="/candidat/offres"
                  className="font-semibold text-[#d03484] underline-offset-4 hover:underline"
                >
                  offres en cours
                </a>
                , ou découvrez notre approche IA dédiée aux candidats avec{' '}
                <a
                  href="/candidat/talentai"
                  className="font-semibold text-[#d03484] underline-offset-4 hover:underline"
                >
                  TalentAI
                </a>
                . Et si vous voulez vraiment comprendre ce qui rend nos process
                différents, lisez notre{' '}
                <a
                  href="/infos/charte"
                  className="font-semibold text-[#d03484] underline-offset-4 hover:underline"
                >
                  charte d’engagement candidat
                </a>
                .
              </p>
            </div>
          </div>
        </Section>
      </SectionBackground>
    </Layout>
  );
}
